import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { useCallback } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { H7 } from 'foundations-components/transitional/components/H7';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Link } from 'foundations-components/transitional/components/Link';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import ACVideoPlayer from 'academy-common-lib/components/video/ACVideoPlayer';
import { HS_VIDEO_SURFACE, trackHsVideoFailedToRender, trackHsVideoPlayClicked, trackHsVideoRenderedSuccessfully } from 'academy-common-lib/utils/hsVideoMetrics';
import OnboardingSectionLayout from './OnboardingSectionLayout';
import { DateIcon } from 'foundations-assets/icons/components/DateIcon';
import { TasksIcon } from 'foundations-assets/icons/components/TasksIcon';
import { FileIcon } from 'foundations-assets/icons/components/FileIcon';
import { SocialSlackIcon } from 'foundations-assets/icons/components/SocialSlackIcon';
import { Metrics } from '../../../Metrics';

// TODO: Replace with onboarding video values
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CREATOR_HUB_ID = 882999426;
const VIDEO_ID = '199658635531';
const RESOURCE_ITEMS = [{
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.tagLiveWorkshops"
  }),
  icon: /*#__PURE__*/_jsx(DateIcon, {
    size: "600"
  })
}, {
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.tagPlaylists"
  }),
  icon: /*#__PURE__*/_jsx(TasksIcon, {
    size: "600"
  })
}, {
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.tagResources"
  }),
  icon: /*#__PURE__*/_jsx(FileIcon, {
    size: "600"
  })
}, {
  label: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.academyOnboarding.tagSlackChannel"
  }),
  icon: /*#__PURE__*/_jsx(SocialSlackIcon, {
    size: "600"
  }),
  href: 'https://join.slack.com/t/academy-onboarding/shared_invite/zt-3uqjghllz-0WJJ0FHPe_xSM434eBo65w'
}];
const ResourceItems = () => {
  return /*#__PURE__*/_jsxs(View, {
    marginBlockStart: "500",
    children: [/*#__PURE__*/_jsx(H7, {
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.academyOnboarding.includes"
      })
    }), /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      flexWrap: "wrap",
      gap: "600",
      children: RESOURCE_ITEMS.map(({
        label,
        icon,
        href
      }, index) => /*#__PURE__*/_jsx(MediaObject, {
        align: "center",
        itemLeft: icon,
        spacing: "extra-small",
        children: href ? /*#__PURE__*/_jsx(Link, {
          external: true,
          href: href,
          typography: "body-125",
          children: label
        }) : /*#__PURE__*/_jsx(Text, {
          children: label
        })
      }, index))
    })]
  });
};
const HeroBanner = () => {
  const {
    isMobile,
    isTablet,
    isLaptop
  } = useMediaQuery();
  const isStacked = isTablet;
  const onHeroHubSpotVideoError = useCallback(e => {
    const detail = e.detail || {};
    const resolvedErrorType = detail.errorType || 'UNKNOWN';
    trackHsVideoFailedToRender(Metrics, {
      errorType: resolvedErrorType,
      surface: HS_VIDEO_SURFACE.ACADEMY_ONBOARDING_HERO
    });
    Raven.captureException(new Error(`HS_VIDEO_FAILED_RENDER_HERO: ${resolvedErrorType}`), {
      tags: {
        hsVideoErrorType: resolvedErrorType
      },
      fingerprint: ['hs-video-failed-render-hero', resolvedErrorType]
    });
  }, []);
  const onHeroHubSpotVideoLoaded = useCallback(() => {
    trackHsVideoRenderedSuccessfully(Metrics, HS_VIDEO_SURFACE.ACADEMY_ONBOARDING_HERO);
  }, []);
  const onHeroHubSpotVideoPlay = useCallback(() => {
    trackHsVideoPlayClicked(Metrics, {
      surface: HS_VIDEO_SURFACE.ACADEMY_ONBOARDING_HERO
    });
  }, []);
  return /*#__PURE__*/_jsx(OnboardingSectionLayout, {
    backgroundColor: "fill-surface-default",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: isStacked ? 'column' : 'row',
      gap: "600",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flex: "1",
        flexDirection: "column",
        maxInlineSize: isStacked ? undefined : isLaptop ? '480px' : '629px',
        children: [/*#__PURE__*/_jsx(H2, {
          marginBlockEnd: isMobile ? undefined : '600',
          typography: isMobile ? 'heading-300' : 'heading-700',
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.heading"
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.description"
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.descriptionCta"
          })
        }), /*#__PURE__*/_jsx(ResourceItems, {})]
      }), /*#__PURE__*/_jsx(View, {
        borderRadius: "400",
        flex: isStacked ? undefined : '1',
        maxInlineSize: isStacked ? undefined : isLaptop ? '390px' : '627px',
        overflow: "hidden",
        children: /*#__PURE__*/_jsx(ACVideoPlayer, {
          creatorHubId: CREATOR_HUB_ID,
          onError: onHeroHubSpotVideoError,
          onLoaded: onHeroHubSpotVideoLoaded,
          onPlay: onHeroHubSpotVideoPlay,
          videoId: VIDEO_ID
        })
      })]
    })
  });
};
export default HeroBanner;